import { map } from 'lodash-es';
import React from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    margin: theme.spacing(1, 0),
  },
  label: {
    marginBottom: theme.spacing(1),
  },
  radio: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  formControlLabelRoot: {
    marginTop: theme.spacing(1),
  },
  formControlLabelLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  radioGroup: {
    '& > *:first-of-type': {
      marginTop: 0,
    },
  },
}));

export type RadioOption = {
  value: string | boolean;
  label: React.ReactNode;
  disabled?: boolean;
};

type Props = {
  name: string;
  options: RadioOption[];
  disabled?: boolean;
  form: {
    touched: Record<string, boolean>;
    errors: Record<string, string>;
    setFieldValue: (name: string, value: unknown) => void;
  };
  label?: string;
  helperText?: string;
  value?: unknown;
  touched?: boolean;
  className?: string;
  [key: string]: unknown;
};

const RadioGroupControl = ({
  name,
  options,
  disabled,
  form,
  label,
  helperText: origHelperText,
  value,
  touched,
  className,
  ...controlProps
}: Props) => {
  const { classes, cx } = useStyles();
  const helperText = ((touched || form.touched[name]) && form.errors[name]) || origHelperText;
  return (
    <FormControl
      component="fieldset"
      error={(touched || form.touched[name]) && Boolean(form.errors[name])}
      className={cx(classes.formControl, className)}
    >
      {label && <FormLabel component="legend" className={classes.label}>{label}</FormLabel>}
      <RadioGroup
        id={name}
        value={value === true || value === false ? value.toString() : value}
        {...controlProps}
        onChange={(e, newValue) => {
          e.preventDefault();
          let translatedValue: string | boolean = newValue;
          if (newValue === 'true') {
            translatedValue = true;
          } else if (newValue === 'false') {
            translatedValue = false;
          }
          form.setFieldValue(name, translatedValue);
        }}
        className={classes.radioGroup}
      >
        {map(options, (option) => {
          return (
            <FormControlLabel
              name={name}
              key={String(option.value)}
              value={
                option.value === true || option.value === false ?
                  option.value.toString() :
                  option.value
              }
              checked={option.value === value}
              control={
                <Radio
                  classes={{ root: classes.radio }}
                />
              }
              label={option.label}
              disabled={disabled || option.disabled}
              classes={{
                root: classes.formControlLabelRoot,
                label: classes.formControlLabelLabel,
              }}
            />
          );
        })}
      </RadioGroup>
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default RadioGroupControl;
